import React, { useEffect, useRef } from 'react';
import {
  Chart,
  BarController,
  BarElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { getMoodColor, toRGBA } from './utils';

// Register Chart.js components
Chart.register(
  BarController,
  BarElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend
);

export interface BarChartProps {
  /** Data values */
  data: number[];
  /** Labels for each bar */
  labels: string[];
  /** Whether to show horizontal bars (default: vertical) */
  horizontal?: boolean;
  /** Container height in pixels */
  height?: number;
  /** Whether to show legend */
  showLegend?: boolean;
  /** Chart title */
  title?: string;
  /** Y-axis minimum value */
  minY?: number;
  /** Y-axis maximum value */
  maxY?: number;
  /** Whether to use mood colors for bars */
  useMoodColors?: boolean;
  /** Custom colors for bars (overrides mood colors) */
  colors?: string[];
  /** Y-axis label */
  yAxisLabel?: string;
  /** X-axis label */
  xAxisLabel?: string;
}

export function BarChart({
  data,
  labels,
  horizontal = false,
  height = 300,
  showLegend = false,
  title,
  minY,
  maxY,
  useMoodColors = true,
  colors,
  yAxisLabel,
  xAxisLabel,
}: BarChartProps) {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Calculate colors
    let backgroundColors: string[];
    let borderColors: string[];

    if (colors) {
      borderColors = colors;
      backgroundColors = colors.map(c => toRGBA(c, 0.9));
    } else if (useMoodColors) {
      borderColors = data.map(value => getMoodColor(value));
      backgroundColors = borderColors.map(c => toRGBA(c, 0.9));
    } else {
      borderColors = data.map(() => 'rgb(102, 126, 234)');
      backgroundColors = borderColors.map(c => toRGBA(c, 0.9));
    }

    const scales: any = {};

    // Y axis config
    if (minY !== undefined || maxY !== undefined || yAxisLabel) {
      scales.y = {};
      if (minY !== undefined) scales.y.min = minY;
      if (maxY !== undefined) scales.y.max = maxY;
      if (yAxisLabel) {
        scales.y.title = { display: true, text: yAxisLabel };
      }
    }

    // X axis config
    if (xAxisLabel) {
      scales.x = {
        title: { display: true, text: xAxisLabel },
      };
    }

    chartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            data,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: 2,
            borderRadius: 5,
            borderSkipped: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: horizontal ? 'y' : 'x',
        plugins: {
          legend: { display: showLegend },
          title: title ? { display: true, text: title } : undefined,
        },
        scales,
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data, labels, horizontal, showLegend, title, minY, maxY, useMoodColors, colors, yAxisLabel, xAxisLabel]);

  return (
    <div style={{ height }}>
      <canvas ref={chartRef} />
    </div>
  );
}

export default BarChart;
